import React, { useState } from "react";
import { useNavigate } from "react-router";
import { useTranslation } from "react-i18next";

import cx from "classnames";
import { useList } from "modules/organization/hooks";
import { useFunctionNamesByLevel, useFunctionNamesByOrg, useFunctionTasksByOrg } from "modules/functionRegister/hooks";
import { ORGANIZATION_CATEGORY_TYPE } from "modules/organizationCategory/constants";

import { Empty } from "antd";

import * as Grid from "components/Grid";

import Card from "components/Card";
import Spacer from "components/Spacer";
import Button from "components/Button";
import Container from "components/Container";

import cls from "./Organization.module.scss";
import Spinner from "components/Spinner";

const Organization = () => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const [type, setType] = useState(ORGANIZATION_CATEGORY_TYPE.MINISTRY_AND_OFFICE);

  const [search] = useState(0);

  const { items, isLoading: isItemsLoading } = useList({
    params: {
      perPage: 8,
      filter: [
        {
          key: "organizationCategory.type",
          operation: "=",
          value: type,
          type: "STRING",
        },
      ],
    },
    enabled: type === ORGANIZATION_CATEGORY_TYPE.GOVERNMENT || type === ORGANIZATION_CATEGORY_TYPE.MINISTRY_AND_OFFICE
  });

  const defaultParams = {
    search,
    perPage: 8,
  };

  const {
    items: functionNamesLevel,
    isFetched: isFunctionNamesLevelFetched,
  } = useFunctionNamesByLevel({
    params: defaultParams,
    enabled: type === ORGANIZATION_CATEGORY_TYPE.FUNCTION_NAME_LEVEL,
  });

  const {
    items: functionNames,
    isFetched: isFunctionNamesFetched,
  } = useFunctionNamesByOrg({
    params: defaultParams,
    enabled: type === ORGANIZATION_CATEGORY_TYPE.FUNCTION_NAME,
  });

  const {
    items: functionTasks,
    isFetched: isFunctionTasksFetched,
    isLoading: isFunctionTasksLoading,
  } = useFunctionTasksByOrg({
    params: defaultParams,
    enabled: type === ORGANIZATION_CATEGORY_TYPE.FUNCTION_TASK,
  });

  return (
    <section className={cls.section}>
      <Container>
        <div className={cls.tabs}>
          {Object.values(ORGANIZATION_CATEGORY_TYPE).map((item) => (
            <div
              key={item}
              className={cx(
                cls.tab,
                type === item && cls.tabActive,
                item === ORGANIZATION_CATEGORY_TYPE.FUNCTION_TASK && cls.tabTasks
              )}
              onClick={() => setType(item)}
            >
              {t(`organizations_${item}`)}
            </div>
          ))}
        </div>

        <Spacer size={50} />

        {/* FUNCTION_TASK */}
        <Grid.Row gutter={[16, 16]}>
          {type === ORGANIZATION_CATEGORY_TYPE.FUNCTION_TASK && (
            isFunctionTasksLoading ? (
              <Grid.Col span={24}>
                <div className={cls.spinner}>
                  <Spinner />
                </div>
              </Grid.Col>
            ) : functionTasks?.length ? (
              functionTasks.map((item) => (
                <Grid.Col span={6} key={item.id}>
                  <Card
                    uuid={item.file.uuid}
                    name={item.name[i18n.language]}
                    count={item.count}
                    onClick={() =>
                      navigate(`/function-tasks?organization_id=${item.id}`)
                    }
                  />
                </Grid.Col>
              ))
            ) : (
              isFunctionTasksFetched && (
                <Grid.Col span={24}>
                  <div className={cls.noData}><Empty description={t('no_data')} /></div>
                </Grid.Col>
              )
            )
          )}
        </Grid.Row>

        {type === ORGANIZATION_CATEGORY_TYPE.FUNCTION_TASK &&
          !!functionTasks?.length && (
            <div className={cls.btn}>
              <Button onClick={() => navigate("/function-task-organizations")}>
                {t("action_more")}
              </Button>
            </div>
          )}

        {/* GOVERNMENT / MINISTRY */}
        <Grid.Row gutter={[24, 24]}>
          {(type === ORGANIZATION_CATEGORY_TYPE.GOVERNMENT ||
            type === ORGANIZATION_CATEGORY_TYPE.MINISTRY_AND_OFFICE) && (
              isItemsLoading ? (
                <Grid.Col span={24}>
                  <div className={cls.spinner}>
                    <Spinner />
                  </div>
                </Grid.Col>
              ) : items?.length ? (
                items.map((item) => (
                  <Grid.Col span={6} key={item.id}>
                    <Card
                      uuid={item.file.uuid}
                      name={item.name[i18n.language]}
                      count={item.count}
                      onClick={() =>
                        navigate(`/function-register?organization_id=${item.id}`)
                      }
                    />
                  </Grid.Col>
                ))
              ) : (
                <Grid.Col span={24}>
                  <div className={cls.noData}><Empty description={t('no_data')} /></div>
                </Grid.Col>
              )
            )}
        </Grid.Row>

        {(type === ORGANIZATION_CATEGORY_TYPE.GOVERNMENT ||
          type === ORGANIZATION_CATEGORY_TYPE.MINISTRY_AND_OFFICE) &&
          !!items?.length && (
            <div className={cls.btn}>
              <Button onClick={() => navigate("/organizations")}>
                {t("action_more")}
              </Button>
            </div>
          )}

        {/* FUNCTION_NAME */}
        <Grid.Row gutter={[16, 16]}>
          {type === ORGANIZATION_CATEGORY_TYPE.FUNCTION_NAME && (
            !isFunctionNamesFetched ? (
              <Grid.Col span={24}>
                <div className={cls.spinner}>
                  <Spinner />
                </div>
              </Grid.Col>
            ) : functionNames?.length ? (
              functionNames.map((item) => (
                <Grid.Col span={6} key={item.id}>
                  <Card
                    uuid={item.file.uuid}
                    name={item.name[i18n.language]}
                    count={item.count}
                    onClick={() =>
                      navigate(`/function-names?organization_id=${item.id}`)
                    }
                  />
                </Grid.Col>
              ))
            ) : (
              <Grid.Col span={24}>
                <div className={cls.noData}><Empty description={t('no_data')} /></div>
              </Grid.Col>
            )
          )}
        </Grid.Row>

        {type === ORGANIZATION_CATEGORY_TYPE.FUNCTION_NAME &&
          !!functionNames?.length && (
            <div className={cls.btn}>
              <Button onClick={() => navigate("/function-name-organizations")}>
                {t("action_more")}
              </Button>
            </div>
          )}

        {/* FUNCTION_NAME_LEVEL */}
        <Grid.Row gutter={[16, 16]}>
          {type === ORGANIZATION_CATEGORY_TYPE.FUNCTION_NAME_LEVEL && (
            !isFunctionNamesLevelFetched ? (
              <Grid.Col span={24}>
                <div className={cls.spinner}>
                  <Spinner />
                </div>
              </Grid.Col>
            ) : functionNamesLevel?.length ? (
              functionNamesLevel.map((item) => (
                <Grid.Col span={6} key={item.id}>
                  <Card
                    name={t(item.level)}
                    count={item.count}
                    onClick={() =>
                      navigate(`/function-register?function_level=${item.level}`)
                    }
                  />
                </Grid.Col>
              ))
            ) : (
              <Grid.Col span={24}>
                <div className={cls.noData}><Empty description={t('no_data')} /></div>
              </Grid.Col>
            )
          )}
        </Grid.Row>
      </Container>
    </section>
  );
};

export default Organization;